/** The tour's identity strip (plan §1 "Identity strip"), read-only, under the header on
 *  every tab — what identifies the tour to the systems that feed and serve it:
 *
 *  - the primary ID (`custom_id`) as a large monospace chip with its value actions (copy,
 *    the ext-IDs and tours grids) and which pipelines use it — import, bookings, API; an
 *    import-bound tour without one says so in amber, as a button to the identifiers;
 *  - the aliases in numbers ("3 · 2 matching · 1 parked") — a button to the identifiers
 *    table;
 *  - the import source and when it last imported ("⟳ Bokun · imported 2 h ago");
 *  - the launch state: the effective policy, own or the operator default's — muted
 *    "policies off" when the policy is not enforced (its owner's config);
 *  - chips: the internal name, Template, Guide group, Deleted.
 *
 *  It shows the STORED tour: the draft is the ledger's and the panels'. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import SyncIcon from '@mui/icons-material/Sync';
import { Box, ButtonBase, Chip, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import type { TourDetail } from '@/api/tours';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { formatRelativeTime } from '@/utils/format';

import { customIdActions, launchPolicyActions, templateFlagActions, type TourCtx } from '../tourLinks';

import { UsedBy } from './panels/IdentifiersBlock';
import { MISSING_PRIMARY } from './readiness';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

function Part({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Box sx={{ display: 'grid', gap: 0.25, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 28, flexWrap: 'wrap' }}>{children}</Box>
    </Box>
  );
}

/** "3 · 2 matching · 1 parked". */
export function aliasSummary(detail: Pick<TourDetail, 'additional_custom_ids'>): string {
  const all = detail.additional_custom_ids.length;
  const matching = detail.additional_custom_ids.filter((a) => a.use_for_matching).length;
  return all === 0 ? 'none' : `${all} · ${matching} matching · ${all - matching} parked`;
}

/** The launch state in words: the effective policy, and whose it is. */
export function launchState(detail: TourDetail): { name: string; source: string; off: boolean } {
  const def = detail.operator_default_launch_policy;
  const eff = detail.launch_policy ?? (def ? { id: def.id, name: def.name } : null);
  return {
    name: eff ? (eff.name ?? `#${eff.id}`) : 'No launch policy',
    source: detail.launch_policy ? 'own' : eff ? 'operator default' : '',
    off: detail.launch_policy_enforced === false,
  };
}

export function IdentityStrip({ detail, onShowIdentifiers }: { detail: TourDetail; onShowIdentifiers: () => void }) {
  const links: TourCtx = { tenantId: detail.tenant?.id };
  const cid = detail.custom_id?.trim() || null;
  const summary = detail.import_summary;
  const source = summary.sources[0];
  const lastRun = summary.runs?.[0];
  const state = launchState(detail);
  const policyRef = detail.launch_policy ?? (detail.operator_default_launch_policy ? { id: detail.operator_default_launch_policy.id, name: detail.operator_default_launch_policy.name } : null);

  return (
    <Box
      component="section"
      aria-label="Identity"
      data-identity-strip
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        columnGap: 3,
        rowGap: 1,
        mb: 2,
        px: 1.5,
        py: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Part label="Primary ID">
        {cid ? (
          <ValueActions chip actions={customIdActions(cid, links)}>
            <Chip
              data-primary-id
              label={cid}
              variant="outlined"
              sx={{ fontFamily: MONO, fontSize: '0.9375rem', fontWeight: 700, height: 28, borderRadius: 1 }}
            />
          </ValueActions>
        ) : summary.is_import_bound ? (
          <ButtonBase
            onClick={onShowIdentifiers}
            data-primary-id
            data-missing
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              borderRadius: 1,
              px: 0.5,
              mx: -0.5,
              typography: 'body2',
              color: 'warning.dark',
              '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
              '&.Mui-focusVisible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
            }}
          >
            <ErrorOutlineIcon aria-hidden sx={{ fontSize: 16 }} />
            {MISSING_PRIMARY}
          </ButtonBase>
        ) : (
          <Typography variant="body2" color="text.disabled" data-primary-id>
            No primary ID
          </Typography>
        )}
        {cid && <UsedBy usedBy={detail.custom_id_used_by} />}
      </Part>

      <Part label="Aliases">
        <ButtonBase
          onClick={onShowIdentifiers}
          aria-label={`Aliases: ${aliasSummary(detail)} — show the identifiers`}
          data-alias-summary
          sx={{
            borderRadius: 1,
            px: 0.5,
            mx: -0.5,
            typography: 'body2',
            '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
            '&.Mui-focusVisible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
          }}
        >
          {aliasSummary(detail)}
        </ButtonBase>
      </Part>

      <Part label="Import">
        {summary.is_import_bound ? (
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-import-source>
            <SyncIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
            {source ? source.name || source.code : 'Imported'}
            {lastRun ? (
              <Tooltip title={lastRun.created_ts}>
                <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'pre' }}>
                  {` · imported ${formatRelativeTime(lastRun.created_ts)}`}
                </Box>
              </Tooltip>
            ) : null}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" data-import-source>
            Not imported
          </Typography>
        )}
      </Part>

      <Part label="Launch state">
        <Typography
          variant="body2"
          component="div"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: state.off ? 'text.secondary' : 'text.primary' }}
          data-launch-state
          data-policies-off={state.off || undefined}
        >
          <RocketLaunchOutlinedIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />
          {policyRef ? (
            <ValueActions actions={launchPolicyActions(policyRef, links)}>{state.name}</ValueActions>
          ) : (
            state.name
          )}
          {state.source && (
            <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'pre' }}>
              {` · ${state.source}`}
            </Box>
          )}
          {state.off && (
            <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'pre' }}>
              {' · policies off'}
            </Box>
          )}
        </Typography>
      </Part>

      {(detail.internal_name || detail.use_as_template || detail.is_staff_chat_group || detail.is_deleted) && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignSelf: 'center' }} data-identity-chips>
          {detail.internal_name && (
            <Tooltip title="Internal name — ops only, never shown to guests">
              <Chip size="small" variant="outlined" label={`Internal: ${detail.internal_name}`} />
            </Tooltip>
          )}
          {detail.use_as_template && (
            <ValueActions chip actions={templateFlagActions(links)}>
              <Chip size="small" color="info" label="Template" />
            </ValueActions>
          )}
          {detail.is_staff_chat_group && <Chip size="small" variant="outlined" label="Guide group" />}
          {detail.is_deleted && <Chip size="small" color="error" label="Deleted" />}
        </Box>
      )}
    </Box>
  );
}
